// =============================================================================
// MODULE NOTE DE DIMENSIONNEMENT (ND)
// Interface de saisie et calcul thermique
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  Calculator, Save, FileCheck, AlertTriangle, CheckCircle2,
  Thermometer, Home, Mountain, Settings, Zap, FileText,
  RefreshCw, Loader2, Lock, History, Download
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Slider } from "../../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../components/ui/alert";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { formatDateFR } from "../../utils/dateFormat";

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs des statuts de couverture - Conformité CEE
const STATUT_COLORS = {
  sous_dimensionne: { bg: "bg-[var(--oh-error-bg)]", text: "text-[var(--oh-error-text)]", border: "border-[var(--oh-error-border)]" },
  conforme: { bg: "bg-[var(--oh-success-bg)]", text: "text-[var(--oh-success-text)]", border: "border-[var(--oh-success-border)]" },
  surdimensionne: { bg: "bg-[var(--oh-info-bg)]", text: "text-[var(--oh-info-text)]", border: "border-[var(--oh-info-border)]" },
  // Legacy
  coherent: { bg: "bg-[var(--oh-success-bg)]", text: "text-[var(--oh-success-text)]", border: "border-[var(--oh-success-border)]" },
};

// Labels des verdicts CEE
const VERDICT_LABELS = {
  conforme: "Conforme CEE",
  sous_dimensionne: "Sous-dimensionné",
  surdimensionne: "Surdimensionné",
  non_calcule: "Non calculé"
};

// Types de régulation
const TYPES_REGULATION = [
  { code: "loi_eau", label: "Loi d'eau" },
  { code: "tout_ou_rien", label: "Tout ou rien" },
  { code: "modulante", label: "Modulante" },
  { code: "classe_iv", label: "Classe IV" },
  { code: "classe_v", label: "Classe V" },
  { code: "classe_vi", label: "Classe VI" },
  { code: "classe_vii", label: "Classe VII" },
  { code: "classe_viii", label: "Classe VIII" },
];

// Types de radiateurs/émetteurs
const TYPES_RADIATEUR = [
  { code: "fonte", label: "Radiateurs fonte" },
  { code: "acier", label: "Radiateurs acier" },
  { code: "aluminium", label: "Radiateurs aluminium" },
  { code: "basse_temperature", label: "Basse température" },
  { code: "plancher_chauffant", label: "Plancher chauffant" },
  { code: "ventilo_convecteur", label: "Ventilo-convecteur" },
];

export default function NoteDimensionnement({ dossier, devis, tenantId = "climatpac" }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  
  // Données du dimensionnement
  const [dimensionnement, setDimensionnement] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  
  // Référentiels
  const [coefficients, setCoefficients] = useState([]);
  const [produits, setProduits] = useState([]);
  
  // Formulaire
  const [formData, setFormData] = useState({
    surface_chauffee: "",
    hauteur_sous_plafond: "2.5",
    code_postal: "",
    zone_climatique: "",
    altitude: "0",
    temperature_interieure: "19",
    temperature_exterieure_base: "",
    coefficient_construction: "renovee_partiellement",
    coefficient_override: null,
    // Nouveaux champs
    type_regulation: "loi_eau",
    type_radiateur: "acier",
    nombre_radiateurs: "",
  });
  
  // Résultats calculés
  const [résultats, setResultats] = useState(null);
  
  // Produit sélectionné
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [simulation, setSimulation] = useState(null);
  
  // Notes
  const [notes, setNotes] = useState("");

  // Fonction pour obtenir les headers avec authentification (toujours frais)
  const getAuthHeaders = () => {
    const token = localStorage.getItem("session_token");
    return {
      "X-Tenant-ID": tenantId || "climatpac",
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  // Charger les données au montage
  useEffect(() => {
    loadInitialData();
  }, [dossier?.id]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Charger les coefficients
      const resCoef = await fetch(`${API}/api/metier/dimensionnement/referentiels/coefficients`, {
        headers: getAuthHeaders()
      });
      if (resCoef.ok) {
        const data = await resCoef.json();
        setCoefficients(data.coefficients || []);
      }

      // Charger les produits PAC
      const resProduits = await fetch(`${API}/api/metier/dimensionnement/produits`, {
        headers: getAuthHeaders()
      });
      if (resProduits.ok) {
        const data = await resProduits.json();
        setProduits(data.produits || []);
      }

      // Charger le dimensionnement existant si disponible
      if (dossier?.id) {
        const resDim = await fetch(`${API}/api/metier/dimensionnement/dossier/${dossier.id}`, {
          headers: getAuthHeaders()
        });
        if (resDim.ok) {
          const dim = await resDim.json();
          if (dim) {
            setDimensionnement(dim);
            setIsLocked(dim.statut === "valide");
            setFormData(dim.logement || formData);
            setResultats(dim.résultats);
            setSelectedProduit(dim.produit_selectionne?.produit_id);
            setSimulation(dim.produit_selectionne);
            setNotes(dim.notes || "");
          }
        }
      }

      // Pré-remplir depuis le dossier
      if (dossier?.adresse_travaux?.code_postal && !formData.code_postal) {
        const cp = dossier.adresse_travaux.code_postal;
        setFormData(prev => ({ ...prev, code_postal: cp }));
        // Auto-déterminer la zone
        autoDetectZone(cp);
      }

    } catch (err) {
      console.error("Erreur chargement:", err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-détection zone climatique
  const autoDetectZone = async (cp) => {
    if (!cp || cp.length !== 5) return;
    try {
      const res = await fetch(`${API}/api/metier/dimensionnement/referentiels/zone-par-cp/${cp}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({
          ...prev,
          zone_climatique: data.zone_climatique,
          temperature_exterieure_base: String(data.temperature_base)
        }));
      }
    } catch (err) {
      console.error("Erreur auto-detect zone:", err);
    }
  };

  // Calcul à la volée
  const handleCalculer = async () => {
    if (!formData.surface_chauffee || !formData.code_postal) {
      toast.error("Veuillez renseigner la surface et le code postal");
      return;
    }
    
    setCalculating(true);
    try {
      const payload = {
        surface_chauffee: parseFloat(formData.surface_chauffee),
        hauteur_sous_plafond: parseFloat(formData.hauteur_sous_plafond) || 2.5,
        code_postal: formData.code_postal,
        zone_climatique: formData.zone_climatique || null,
        altitude: parseInt(formData.altitude) || 0,
        temperature_interieure: parseFloat(formData.temperature_interieure) || 19,
        temperature_exterieure_base: formData.temperature_exterieure_base ? parseFloat(formData.temperature_exterieure_base) : null,
        coefficient_construction: formData.coefficient_construction,
        coefficient_override: formData.coefficient_override ? parseFloat(formData.coefficient_override) : null
      };

      const res = await fetch(`${API}/api/metier/dimensionnement/calculer`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        setResultats(data.résultats);
        // Mettre à jour les valeurs auto-complétées
        setFormData(prev => ({
          ...prev,
          zone_climatique: data.logement.zone_climatique,
          temperature_exterieure_base: String(data.logement.temperature_exterieure_base)
        }));
        toast.success("Calcul effectué");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors du calcul");
      }
    } catch (err) {
      console.error("Erreur calcul:", err);
      toast.error("Erreur lors du calcul");
    } finally {
      setCalculating(false);
    }
  };

  // Simulation produit
  const handleSimulerProduit = async (produitId) => {
    if (!résultats || !produitId) return;
    
    setSelectedProduit(produitId);
    const produit = produits.find(p => p.id === produitId);
    
    if (produit && résultats) {
      // Calcul local du taux de couverture
      const puissance = produit.puissance_a_t_base || produit.puissance_nominale;
      const taux = (puissance / résultats.deperditions_kw) * 100;
      
      let statut = "coherent";
      if (taux < 90) statut = "sous_dimensionne";
      else if (taux > 130) statut = "surdimensionne";
      
      setSimulation({
        produit_id: produitId,
        marque: produit.marque,
        référence: produit.référence,
        puissance_nominale: produit.puissance_nominale,
        puissance_a_t_base: produit.puissance_a_t_base,
        taux_couverture: Math.round(taux * 10) / 10,
        statut
      });
    }
  };

  // Sauvegarder le dimensionnement
  const handleSave = async () => {
    if (!résultats) {
      toast.error("Veuillez d'abord effectuer un calcul");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        dossier_id: dossier?.id,
        devis_id: devis?.id,
        logement: {
          surface_chauffee: parseFloat(formData.surface_chauffee),
          hauteur_sous_plafond: parseFloat(formData.hauteur_sous_plafond) || 2.5,
          code_postal: formData.code_postal,
          zone_climatique: formData.zone_climatique,
          altitude: parseInt(formData.altitude) || 0,
          temperature_interieure: parseFloat(formData.temperature_interieure) || 19,
          temperature_exterieure_base: parseFloat(formData.temperature_exterieure_base),
          coefficient_construction: formData.coefficient_construction,
          coefficient_override: formData.coefficient_override ? parseFloat(formData.coefficient_override) : null
        }
      };

      let res;
      if (dimensionnement?.id) {
        // Mise à jour
        res = await fetch(`${API}/api/metier/dimensionnement/${dimensionnement.id}`, {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ logement: payload.logement, notes })
        });
      } else {
        // Création
        res = await fetch(`${API}/api/metier/dimensionnement`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        });
      }

      if (res.ok) {
        const dim = await res.json();
        setDimensionnement(dim);
        toast.success("Dimensionnement enregistré");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de l'enregistrement");
      }
    } catch (err) {
      console.error("Erreur save:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Valider le dimensionnement
  const handleValider = async () => {
    if (!dimensionnement?.id || !simulation) {
      toast.error("Veuillez d'abord sauvegarder et sélectionner un produit");
      return;
    }
    
    setSaving(true);
    try {
      // D'abord mettre à jour le produit sélectionné
      await fetch(`${API}/api/metier/dimensionnement/${dimensionnement.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ produit_selectionne_id: selectedProduit })
      });
      
      // Puis valider
      const res = await fetch(`${API}/api/metier/dimensionnement/${dimensionnement.id}/valider`, {
        method: "POST",
        headers: getAuthHeaders()
      });

      if (res.ok) {
        const dim = await res.json();
        setDimensionnement(dim);
        setIsLocked(true);
        toast.success("Dimensionnement validé et verrouillé");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de la validation");
      }
    } catch (err) {
      console.error("Erreur validation:", err);
      toast.error("Erreur lors de la validation");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-detect zone si code postal change
    if (field === "code_postal" && value.length === 5) {
      autoDetectZone(value);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-medium text-[var(--oh-text)] flex items-center gap-2">
              <Calculator className="w-5 h-5 text-cyan-400" />
              Note de Dimensionnement
              {dimensionnement?.version && (
                <Badge variant="outline" className="ml-2">v{dimensionnement.version}</Badge>
              )}
            </h2>
            <p className="text-[var(--oh-text-muted)] text-sm mt-1">
              Calcul des déperditions thermiques et dimensionnement PAC
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isLocked ? (
              <Badge className="bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] border-[var(--oh-accent)]">
                <Lock className="w-3 h-3 mr-1" />
                Validé
              </Badge>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleSave}
                  disabled={saving || !résultats}
                  className="border-[var(--oh-border)] text-[var(--oh-text-muted)] hover:bg-[var(--oh-bg-elevated)]"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
                <Button
                  onClick={handleValider}
                  disabled={saving || !dimensionnement?.id || !simulation}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <FileCheck className="w-4 h-4 mr-2" />
                  Valider
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Alerte si verrouillé */}
        {isLocked && (
          <Alert className="bg-[var(--oh-accent-bg)] border-[var(--oh-accent)]">
            <Lock className="w-4 h-4 text-[var(--oh-accent-text)]" />
            <AlertTitle className="text-[var(--oh-accent-text)]">Dimensionnement verrouillé</AlertTitle>
            <AlertDescription className="text-[var(--oh-accent-text)]">
              Ce dimensionnement a été validé le {formatDateFR(dimensionnement?.validated_at)} par {dimensionnement?.validated_by}.
              Pour modifier, créez une nouvelle version.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Colonne gauche - Données logement */}
          <div className="space-y-6">
            {/* Données logement */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader>
                <CardTitle className="text-[var(--oh-text)] text-lg flex items-center gap-2">
                  <Home className="w-5 h-5 text-cyan-400" />
                  Données Logement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[var(--oh-text-muted)] text-sm">Surface chauffée (m²) *</Label>
                    <Input
                      type="number"
                      value={formData.surface_chauffee}
                      onChange={(e) => updateField("surface_chauffee", e.target.value)}
                      placeholder="120"
                      disabled={isLocked}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[var(--oh-text-muted)] text-sm">Hauteur sous plafond (m)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.hauteur_sous_plafond}
                      onChange={(e) => updateField("hauteur_sous_plafond", e.target.value)}
                      placeholder="2.5"
                      disabled={isLocked}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[var(--oh-text-muted)] text-sm">Code postal *</Label>
                    <Input
                      value={formData.code_postal}
                      onChange={(e) => updateField("code_postal", e.target.value)}
                      placeholder="81000"
                      maxLength={5}
                      disabled={isLocked}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[var(--oh-text-muted)] text-sm">Zone climatique</Label>
                    <Input
                      value={formData.zone_climatique}
                      onChange={(e) => updateField("zone_climatique", e.target.value)}
                      placeholder="H2c"
                      disabled={isLocked}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[var(--oh-text-muted)] text-sm">Altitude (m)</Label>
                    <Input
                      type="number"
                      value={formData.altitude}
                      onChange={(e) => updateField("altitude", e.target.value)}
                      placeholder="0"
                      disabled={isLocked}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[var(--oh-text-muted)] text-sm">T° extérieure base (°C)</Label>
                    <Input
                      type="number"
                      value={formData.temperature_exterieure_base}
                      onChange={(e) => updateField("temperature_exterieure_base", e.target.value)}
                      placeholder="-7"
                      disabled={isLocked}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[var(--oh-text-muted)] text-sm">T° intérieure souhaitée (°C)</Label>
                  <Input
                    type="number"
                    value={formData.temperature_interieure}
                    onChange={(e) => updateField("temperature_interieure", e.target.value)}
                    placeholder="19"
                    disabled={isLocked}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Coefficient de construction */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader>
                <CardTitle className="text-[var(--oh-text)] text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[var(--oh-info-text)]" />
                  Coefficient de Construction
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-sm">Type de construction</Label>
                  <Select
                    value={formData.coefficient_construction}
                    onValueChange={(v) => updateField("coefficient_construction", v)}
                    disabled={isLocked}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {coefficients.map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-[var(--oh-text-muted)] text-sm">Override manuel (optionnel)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.coefficient_override || ""}
                    onChange={(e) => updateField("coefficient_override", e.target.value || null)}
                    placeholder="Laisser vide pour utiliser la valeur standard"
                    disabled={isLocked}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Émission de chaleur */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader>
                <CardTitle className="text-[var(--oh-text)] text-lg flex items-center gap-2">
                  <Thermometer className="w-5 h-5 text-[var(--oh-info-text)]" />
                  Émission de Chaleur
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-sm">Type de régulation</Label>
                  <Select
                    value={formData.type_regulation}
                    onValueChange={(v) => updateField("type_regulation", v)}
                    disabled={isLocked}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES_REGULATION.map(r => (
                        <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[var(--oh-text-muted)] text-sm">Type d'émetteurs</Label>
                    <Select
                      value={formData.type_radiateur}
                      onValueChange={(v) => updateField("type_radiateur", v)}
                      disabled={isLocked}
                    >
                      <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TYPES_RADIATEUR.map(r => (
                          <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[var(--oh-text-muted)] text-sm">Nombre d'émetteurs</Label>
                    <Input
                      type="number"
                      value={formData.nombre_radiateurs}
                      onChange={(e) => updateField("nombre_radiateurs", e.target.value)}
                      placeholder="8"
                      disabled={isLocked}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bouton calculer */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardContent className="pt-6">
                {!isLocked && (
                  <Button
                    onClick={handleCalculer}
                    disabled={calculating}
                    className="w-full bg-cyan-600 hover:bg-cyan-700"
                  >
                    {calculating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Calculator className="w-4 h-4 mr-2" />
                    )}
                    Calculer les déperditions
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Colonne droite - Résultats */}
          <div className="space-y-6">
            {/* Résultats calcul */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader>
                <CardTitle className="text-[var(--oh-text)] text-lg flex items-center gap-2">
                  <Thermometer className="w-5 h-5 text-[var(--oh-error-text)]" />
                  Résultats du Calcul
                  {résultats && (
                    <Badge className="bg-[var(--oh-stat-cyan-bg)] text-[var(--oh-stat-cyan-text)] ml-2">CEE</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {résultats ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-[var(--oh-bg-surface)] rounded-lg p-4 text-center">
                        <div className="text-[var(--oh-text-muted)] text-sm">Volume</div>
                        <div className="text-2xl font-medium text-[var(--oh-text)]">{résultats.volume} m³</div>
                      </div>
                      <div className="bg-[var(--oh-bg-surface)] rounded-lg p-4 text-center">
                        <div className="text-[var(--oh-text-muted)] text-sm">Coefficient utilisé</div>
                        <div className="text-2xl font-medium text-[var(--oh-text)]">{résultats.coefficient_utilise}</div>
                      </div>
                    </div>

                    {/* Températures CEE */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] rounded-lg p-3 text-center">
                        <div className="text-[var(--oh-accent-text)] text-xs">T° base</div>
                        <div className="text-lg font-medium text-[var(--oh-accent-text)]">{résultats.t_base || formData.temperature_exterieure_base}°C</div>
                      </div>
                      <div className="bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] rounded-lg p-3 text-center">
                        <div className="text-[var(--oh-accent-text)] text-xs">T° référence</div>
                        <div className="text-lg font-medium text-[var(--oh-accent-text)]">{résultats.t_ref || "N/A"}°C</div>
                      </div>
                      <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3 text-center">
                        <div className="text-[var(--oh-text-muted)] text-xs">T° arrêt PAC</div>
                        <div className="text-lg font-medium text-[var(--oh-text-muted)]">{résultats.t_arret_pac || "-7"}°C</div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 rounded-lg p-4 text-center border border-cyan-500/30">
                      <div className="text-cyan-400 text-sm">Déperditions</div>
                      <div className="text-3xl font-medium text-[var(--oh-text)]">{résultats.deperditions_kw} kW</div>
                      <div className="text-[var(--oh-text-muted)] text-sm">({résultats.deperditions_w} W)</div>
                    </div>

                    {/* Bornes CEE P60/P140 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)] rounded-lg p-4 text-center">
                        <div className="text-[var(--oh-success-text)] text-sm">P60 (min CEE)</div>
                        <div className="text-xl font-medium text-[var(--oh-success-text)]">{résultats.p60_kw || résultats.puissance_60} kW</div>
                        <div className="text-[var(--oh-success-text)] text-xs">60% des déperditions</div>
                      </div>
                      <div className="bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)] rounded-lg p-4 text-center">
                        <div className="text-[var(--oh-info-text)] text-sm">P140 (max CEE)</div>
                        <div className="text-xl font-medium text-[var(--oh-info-text)]">{résultats.p140_kw || (résultats.deperditions_kw * 1.4).toFixed(2)} kW</div>
                        <div className="text-[var(--oh-info-text)] text-xs">140% des déperditions</div>
                      </div>
                    </div>

                    <Alert className="bg-[var(--oh-accent-bg)] border-[var(--oh-accent)]">
                      <AlertDescription className="text-[var(--oh-accent-text)] text-sm">
                        <strong>Conformité CEE :</strong> La puissance de la PAC doit être comprise entre P60 ({résultats.p60_kw || résultats.puissance_60} kW) et P140 ({résultats.p140_kw || (résultats.deperditions_kw * 1.4).toFixed(2)} kW).
                      </AlertDescription>
                    </Alert>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[var(--oh-text-muted)]">
                    <Calculator className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Renseignez les données et cliquez sur "Calculer"</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sélection PAC */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader>
                <CardTitle className="text-[var(--oh-text)] text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[var(--oh-info-text)]" />
                  Sélection PAC
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {résultats ? (
                  <>
                    <div>
                      <Label className="text-[var(--oh-text-muted)] text-sm">Produit PAC</Label>
                      <Select
                        value={selectedProduit || ""}
                        onValueChange={handleSimulerProduit}
                        disabled={isLocked}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1">
                          <SelectValue placeholder="Sélectionner une PAC" />
                        </SelectTrigger>
                        <SelectContent>
                          {produits.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.marque} {p.référence} - {p.puissance_nominale} kW
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {simulation && (
                      <div className={`rounded-lg p-4 ${STATUT_COLORS[simulation.verdict_cee || simulation.statut]?.bg || STATUT_COLORS[simulation.statut]?.bg} border ${STATUT_COLORS[simulation.verdict_cee || simulation.statut]?.border || STATUT_COLORS[simulation.statut]?.border}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`font-medium ${STATUT_COLORS[simulation.verdict_cee || simulation.statut]?.text || STATUT_COLORS[simulation.statut]?.text}`}>
                            {simulation.marque} {simulation.référence}
                          </span>
                          <div className="flex gap-2">
                            <Badge className={`${STATUT_COLORS[simulation.verdict_cee || simulation.statut]?.bg || STATUT_COLORS[simulation.statut]?.bg} ${STATUT_COLORS[simulation.verdict_cee || simulation.statut]?.text || STATUT_COLORS[simulation.statut]?.text}`}>
                              {simulation.taux_couverture}%
                            </Badge>
                            {simulation.verdict_cee && (
                              <Badge className={`${simulation.verdict_cee === 'conforme' ? 'bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]' : simulation.verdict_cee === 'sous_dimensionne' ? 'bg-[var(--oh-error-bg)] text-[var(--oh-error-text)]' : 'bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]'}`}>
                                {VERDICT_LABELS[simulation.verdict_cee] || simulation.verdict_cee}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-[var(--oh-text-muted)]">
                          Puissance nominale: {simulation.puissance_nominale} kW
                          {simulation.puissance_a_t_base && (
                            <span> | À T° base: {simulation.puissance_a_t_base} kW</span>
                          )}
                        </div>
                        {/* Détails techniques */}
                        {(simulation.cop_nominal || simulation.scop || simulation.etas) && (
                          <div className="text-sm text-[var(--oh-text-muted)] mt-1">
                            {simulation.cop_nominal && <span>COP: {simulation.cop_nominal} | </span>}
                            {simulation.scop && <span>SCOP: {simulation.scop} | </span>}
                            {simulation.etas && <span>ETAS: {simulation.etas}%</span>}
                          </div>
                        )}
                        
                        {/* Verdict CEE */}
                        <div className={`mt-3 text-sm ${STATUT_COLORS[simulation.verdict_cee || simulation.statut]?.text || STATUT_COLORS[simulation.statut]?.text}`}>
                          {(simulation.verdict_cee === "sous_dimensionne" || simulation.statut === "sous_dimensionne") && (
                            <span className="flex items-center gap-1">
                              <AlertTriangle className="w-4 h-4" />
                              Sous-dimensionné - Puissance insuffisante (&lt; P60)
                            </span>
                          )}
                          {(simulation.verdict_cee === "conforme" || simulation.statut === "coherent") && (
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="w-4 h-4" />
                              {simulation.verdict_label || "Conforme CEE - Puissance dans la plage P60-P140"}
                            </span>
                          )}
                          {(simulation.verdict_cee === "surdimensionne" || simulation.statut === "surdimensionne") && (
                            <span className="flex items-center gap-1">
                              <AlertTriangle className="w-4 h-4" />
                              Surdimensionné - Puissance trop élevée (&gt; P140)
                            </span>
                          )}
                        </div>

                        {/* Couverture annuelle PAC hybride */}
                        {simulation.couverture_annuelle_pct !== null && simulation.couverture_annuelle_pct !== undefined && (
                          <div className={`mt-2 p-2 rounded ${simulation.couverture_annuelle_pct >= 80 ? 'bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]' : 'bg-[var(--oh-error-bg)] border border-[var(--oh-error-border)]'}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-[var(--oh-text-muted)] text-sm">Couverture annuelle (PAC hybride):</span>
                              <Badge className={simulation.couverture_annuelle_pct >= 80 ? 'bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]' : 'bg-[var(--oh-error-bg)] text-[var(--oh-error-text)]'}>
                                {simulation.couverture_annuelle_pct}%
                                {simulation.couverture_annuelle_pct >= 80 ? ' ✓ (≥80%)' : ' ✗ (<80%)'}
                              </Badge>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {produits.length === 0 && (
                      <Alert className="bg-[var(--oh-info-bg)] border-[var(--oh-info-border)]">
                        <AlertTriangle className="w-4 h-4 text-[var(--oh-info-text)]" />
                        <AlertDescription className="text-[var(--oh-info-text)]">
                          Aucun produit PAC dans le catalogue. Ajoutez des produits pour simuler.
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4 text-[var(--oh-text-muted)]">
                    <p>Effectuez d'abord un calcul pour sélectionner une PAC</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader>
                <CardTitle className="text-[var(--oh-text)] text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[var(--oh-text-muted)]" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Observations, remarques..."
                  disabled={isLocked}
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] min-h-[100px]"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
